import { useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { Eyebrow } from "../../components/ui/Eyebrow";
import { Chip } from "../../components/ui/Chip";
import { useMeasurements } from "../measurements/useMeasurements";
import { formatDate, formatNumber } from "../../lib/format";

type MetricKey = "waist_cm" | "chest_cm" | "weight_kg";

const METRICS: { key: MetricKey; label: string; unit: string }[] = [
  { key: "waist_cm", label: "Cintura", unit: "cm" },
  { key: "chest_cm", label: "Pecho", unit: "cm" },
  { key: "weight_kg", label: "Peso", unit: "kg" },
];

const W = 320;
const H = 130;
const PAD_X = 20;
const PAD_TOP = 12;
const PAD_BOTTOM = 18;

export function EvolutionChart() {
  const { history, loading } = useMeasurements();
  const [metric, setMetric] = useState<MetricKey>("waist_cm");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  // Últimos 6 registros, en orden cronológico ascendente para dibujar de izquierda a derecha.
  const points = useMemo(() => {
    return [...history]
      .slice(0, 6)
      .reverse()
      .map((m) => ({ date: m.date, value: m[metric] }))
      .filter((p): p is { date: string; value: number } => p.value != null);
  }, [history, metric]);

  const activeMetric = METRICS.find((m) => m.key === metric)!;

  if (loading) return null;

  if (points.length < 2) {
    return (
      <Card>
        <Eyebrow>Evolución · últimas semanas</Eyebrow>
        <p className="text-sm text-muted">Registra medidas al menos 2 semanas para ver la evolución.</p>
      </Card>
    );
  }

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const usableWidth = W - PAD_X * 2;
  const usableHeight = H - PAD_TOP - PAD_BOTTOM;

  const coords = points.map((p, i) => {
    const x = PAD_X + (i / (points.length - 1)) * usableWidth;
    const y = PAD_TOP + (1 - (p.value - min) / range) * usableHeight;
    return { ...p, x, y };
  });

  const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(1)},${H - PAD_BOTTOM} L${coords[0].x.toFixed(1)},${H - PAD_BOTTOM} Z`;

  const first = points[0].value;
  const last = points[points.length - 1].value;
  const delta = last - first;
  const deltaLabel = `${delta <= 0 ? "▼" : "▲"} ${formatNumber(Math.abs(delta))} ${activeMetric.unit}`;

  const hovered = hoverIndex != null ? coords[hoverIndex] : null;

  function handlePointer(clientX: number, svg: SVGSVGElement) {
    const rect = svg.getBoundingClientRect();
    const relX = ((clientX - rect.left) / rect.width) * W;
    let nearest = 0;
    let nearestDist = Infinity;
    coords.forEach((c, i) => {
      const d = Math.abs(c.x - relX);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = i;
      }
    });
    setHoverIndex(nearest);
  }

  return (
    <Card className="chart">
      <Eyebrow right={<Chip>{deltaLabel}</Chip>}>Evolución · últimas semanas</Eyebrow>
      <div className="mb-3.5 flex gap-1.5">
        {METRICS.map((m) => (
          <button
            key={m.key}
            onClick={() => {
              setMetric(m.key);
              setHoverIndex(null);
            }}
            className={`rounded-full border px-3 py-1.5 font-mono text-[10px] tracking-[0.08em] ${
              metric === m.key
                ? "border-accent bg-accent text-white shadow-[0_4px_14px_rgba(46,107,255,.4)]"
                : "border-line text-muted"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full"
        onPointerMove={(e) => handlePointer(e.clientX, e.currentTarget)}
        onPointerLeave={() => setHoverIndex(null)}
      >
        <defs>
          <linearGradient id="area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2E6BFF" stopOpacity=".28" />
            <stop offset="100%" stopColor="#2E6BFF" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.55, 0.85].map((f) => (
          <line
            key={f}
            x1={PAD_X}
            y1={PAD_TOP + usableHeight * f}
            x2={W - PAD_X}
            y2={PAD_TOP + usableHeight * f}
            stroke="rgba(138,147,168,.12)"
          />
        ))}
        <path fill="url(#area)" d={areaPath} />
        <path
          d={linePath}
          stroke="#2E6BFF"
          strokeWidth={2.4}
          fill="none"
          strokeLinecap="round"
          style={{ filter: "drop-shadow(0 2px 6px rgba(46,107,255,.5))" }}
        />
        {coords.map((c, i) => (
          <circle
            key={c.date}
            cx={c.x}
            cy={c.y}
            r={i === coords.length - 1 ? 4.5 : 3.5}
            fill={i === coords.length - 1 ? "#6FA0FF" : "#0B101E"}
            stroke="#6FA0FF"
            strokeWidth={2}
          />
        ))}
        {hovered && (
          <line x1={hovered.x} y1={PAD_TOP} x2={hovered.x} y2={H - PAD_BOTTOM} stroke="#3BE0FF" strokeWidth={1} strokeDasharray="2,3" />
        )}
        <text x={coords[coords.length - 1].x} y={coords[coords.length - 1].y - 9} textAnchor="end" className="fill-accent-bright font-mono text-[10px]">
          {formatNumber(last)}
        </text>
        {coords.map((c) => (
          <text key={c.date} x={c.x} y={H - 4} textAnchor="middle" className="fill-muted font-mono text-[9px]">
            {formatDate(c.date)}
          </text>
        ))}
      </svg>

      {hovered && (
        <p className="mt-1 text-center font-mono text-[11px] text-accent-bright">
          {formatDate(hovered.date)} · {formatNumber(hovered.value)} {activeMetric.unit}
        </p>
      )}
    </Card>
  );
}
